import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Tablet, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function TabletLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) { toast.error("Praxis-Code ist Pflicht."); return; }
    setLoading(true);
    try {
      const data = await login(email, password, code.trim());
      if (data.mode !== "kiosk") {
        toast.error("Praxis-Code ungültig.");
        return;
      }
      localStorage.removeItem("auth_token");
      navigate("/patient", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Anmeldung fehlgeschlagen.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-emerald-950 grid place-items-center px-6 py-10">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 md:p-10">
        <div className="w-12 h-12 rounded-xl bg-emerald-900 text-white grid place-items-center mb-5">
          <Tablet className="w-6 h-6" />
        </div>
        <h1 className="font-display text-3xl font-semibold text-emerald-900">Tablet-Login</h1>
        <p className="text-stone-500 mt-1 mb-8">
          Für den Patientenmodus. Praxis-Code ist Pflicht.
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-base font-medium text-stone-800">Praxis-E-Mail</Label>
            <Input id="email" data-testid="tablet-login-email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} className="h-14 px-4 text-lg rounded-xl mt-2" autoComplete="email" />
          </div>
          <div>
            <Label htmlFor="password" className="text-base font-medium text-stone-800">Passwort</Label>
            <Input id="password" data-testid="tablet-login-password" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)} className="h-14 px-4 text-lg rounded-xl mt-2" autoComplete="current-password" />
          </div>
          <div>
            <Label htmlFor="code" className="text-base font-medium text-stone-800">Praxis-Code *</Label>
            <Input id="code" data-testid="tablet-login-code" type="text" required value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={8} placeholder="z. B. 7K3M9X2P"
              className="h-14 px-4 text-lg rounded-xl mt-2 tracking-widest font-mono uppercase" />
          </div>
          <Button type="submit" data-testid="tablet-login-submit" disabled={loading}
            className="w-full h-16 text-lg rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
            <ArrowRight className="mr-2 w-5 h-5" /> {loading ? "Wird gestartet…" : "Patientenmodus starten"}
          </Button>
        </form>

        <p className="mt-6 text-xs text-stone-400 text-center">
          Sind Sie Praxis-Mitarbeiter:in?{" "}
          <Link to="/login" data-testid="link-admin-login" className="text-stone-500 hover:underline">Hier zum Admin-Login</Link>
        </p>
      </div>
    </div>
  );
}
